import * as React from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Creators as procedureActions } from '@/domain/useCases/procedures/getProcedureInformation';
import { Creators as registerEmployabilityCharterActions } from '@/domain/useCases/procedures/registerEmployabilityCharter';
import { SubContainer } from '../../../certificates-constancies/type1/info/components';
import {
  ContainerProcedure,
  ButtonBack
} from '../../ChangeCareerCampusModality/info/components';
import Processing from '@/presentation/components/Processing';
import {
  TextTitle,
  ToggleHeader
} from '../../../certificates-constancies/type1/register/components';
import { ProcedureInfoComponent } from '../info/components/ProcedureInfoComponent';
import Steps from './components/Steps';
import { fields } from './types';
import MyModal from '@/presentation/modules/procedures/components/Modals/MyModal';
import useProfile from '@/presentation/hooks/useProfile';
import useExternalSettings from '@/presentation/hooks/useExternalSettings';
import routes from '@/presentation/components/Routes/routingMap';
import imgWarning from '../../../../../../../assets/illustrations/warning.svg';
import iconClock from '../../../../../../../assets/illustrations/exists-procedure.svg';
import { RegisterEmployabilityCharterRequest } from '@/domain/entities/procedures/RegisterEmployabilityCharterRequest';

export const ElementClaim = styled.button`
  padding: 2px;
  color: #5b36f2;
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;

  &:focus {
    outline: 1.5px solid #5b36f2;
    border-radius: 4px;
    color: #3a1ea1;
  }
`;

const EmployabilityCharterRegisterViewController = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { academic } = useProfile();
  const { currentPeriod } = useExternalSettings();

  const { procedure: procedureInfo } = useSelector(
    (state: any) => state.procedureInformation
  );
  const {
    isSuccess: isSuccessRegister,
    data: requestId,
    isLoading: isLoadingRegister,
    error: isError
  } = useSelector((state: any) => state.registerEmployabilityCharter);

  const isDuplicateRegister = requestId === -9999;

  const { procedure: procedureSelectedInfo, isLoading } = useSelector(
    (state: any) => state.procedureInformation
  );

  const [studentType, setStudentType] = React.useState('');
  const [selectedDocument, setSelectedDocument] = React.useState(null);
  const [selectedDocumentFields, setSelectedDocumentFields] =
    React.useState<any>(null);
  const [formIsCompleted, setFormIsCompleted] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<any>(null);
  const paramClaims = 'TA0047';
  const redirectToTicket = () => {
    history.push(
      `${routes.student.procedures.categories.tickets.register}?v=${paramClaims}`
    );
  };

  const handlerChangeDocument = (doc: any) => {
    setSelectedDocument(doc);
  };

  const handlerChangeField = (key: string, value: string) => {
    setSelectedDocumentFields({
      ...selectedDocumentFields,
      [key]: { ...selectedDocumentFields[key], value }
    });
  };

  const handlerErrors = (key: string, value: boolean) => {
    setFieldErrors({
      ...fieldErrors,
      [key]: value
    });
  };

  const handlerRegister = () => {
    const doc = procedureInfo?.detail?.employabilityCharters.find(
      (d: any) => d.id === selectedDocument
    );
    const requestData: RegisterEmployabilityCharterRequest = {
      acadCareer: academic.career,
      campus: academic.campus,
      charterDesc: doc?.description || '',
      charterType: doc?.id || '',
      companyName: selectedDocumentFields.companyName.value,
      duration: selectedDocumentFields?.duration?.value || '',
      periodId: currentPeriod,
      position: selectedDocumentFields.position.value,
      representative: selectedDocumentFields.representative.value,
      studentType,
      periodRelativeCP: procedureInfo?.periodRelativeCP || ''
    };
    dispatch(
      registerEmployabilityCharterActions.registerEmployabilityCharter(
        requestData.acadCareer,
        requestData.campus,
        requestData.charterDesc,
        requestData.charterType,
        requestData.companyName,
        requestData.duration,
        requestData.periodId,
        requestData.position,
        requestData.representative,
        requestData.studentType,
        requestData.periodRelativeCP
      )
    );
  };

  const handlerBackButton = () => {
    history.goBack();
  };

  const hasErros = () => Object.keys(fieldErrors).some((e) => !!fieldErrors[e]);

  React.useEffect(
    () => () => {
      dispatch(
        registerEmployabilityCharterActions.registerEmployabilityCharterClear()
      );
    },
    []
  );

  const getProcedurenfo = (studentGraduatedType: 'EGRE' | '') => {
    if (!procedureSelectedInfo && !isLoading)
      dispatch(
        procedureActions.getProcedureInformation(1038, studentGraduatedType)
      );
  };

  React.useEffect(() => {
    if (academic) {
      const { isGraduated, hasBachelor } = academic;
      let type = isGraduated ? 'EGRE' : 'MATR';
      if (type === 'EGRE' && hasBachelor) type = 'BACH';
      setStudentType(type);
      getProcedurenfo(isGraduated ? 'EGRE' : '');
    }
  }, [academic]);

  React.useEffect(() => {
    if (selectedDocument) {
      const doc = procedureInfo?.detail?.employabilityCharters.find(
        (d: any) => d.id === selectedDocument
      );
      let fieldsSelected;
      if (
        doc?.description.includes('prácticas') ||
        doc?.description.includes('internado')
      ) {
        fieldsSelected = fields.PRACTICES;
      }
      if (doc?.description.includes('empleo')) {
        fieldsSelected = fields.EMPLOYMENT;
      }
      setSelectedDocumentFields(fieldsSelected);
    }
  }, [selectedDocument]);

  React.useEffect(() => {
    if (selectedDocumentFields) {
      const emptyFieds = Object.keys(selectedDocumentFields).filter(
        (f) => !selectedDocumentFields[f].value
      );
      setFormIsCompleted(emptyFieds.length === 0);
    }
  }, [selectedDocumentFields]);

  return (
    <div data-testid="cmp-employabilityCharterView">
      <ContainerProcedure>
        <Processing isOpen={isLoadingRegister} />
        <SubContainer>
          <ButtonBack onClick={handlerBackButton} />
        </SubContainer>
        <SubContainer>
          <ToggleHeader
            inRegister
            name={procedureInfo?.name}
            amount={procedureInfo?.amount}
            duration={procedureInfo?.duration}
            childContent={<ProcedureInfoComponent pin1={procedureInfo?.pin1} />}
            twoLineFormat
          />
          <TextTitle title="Progreso" />
          <Steps
            steps={procedureInfo?.steps || []}
            selectedDocument={selectedDocument}
            documents={procedureInfo?.detail?.employabilityCharters}
            selectedDocumentFields={selectedDocumentFields}
            handlerChangeDocument={handlerChangeDocument}
            setSelectedDocumentFields={handlerChangeField}
            formIsOk={formIsCompleted && !hasErros()}
            handlerErrors={handlerErrors}
            handlerRegister={handlerRegister}
          />
        </SubContainer>
        <MyModal
          img={iconClock}
          data={{
            description:
              'El estado del trámite se actualizará en unos minutos cuando tu carta esté disponible para descargar.',
            title: 'Estamos procesando tu solicitud'
          }}
          handlerClick={() => {
            history.push(
              routes.student.procedures.home
              /* routes.student.procedures.categories.requests.employabilityCharter
                .progress */
            );
          }}
          handlerClose={() => {
            history.push(routes.student.procedures.home);
          }}
          show={isSuccessRegister && !isError && !isDuplicateRegister}
        />
        <MyModal
          img={imgWarning}
          data={{
            description: (
              <>Parece que hubo un error y no pudimos generar tu solicitud.</>
            ),
            description2: (
              <>
                Si el problema continúa, escríbenos a{' '}
                <ElementClaim onClick={redirectToTicket}>
                  registro de quejas y reclamos.
                </ElementClaim>
              </>
            ),
            title: '¡Oh no!'
          }}
          handlerClick={() => {
            history.push(
              routes.student.procedures.categories.requests.employabilityCharter
                .info
            );
          }}
          handlerClose={() => {
            history.push(
              routes.student.procedures.categories.requests.employabilityCharter
                .info
            );
          }}
          show={isError}
        />
        <MyModal
          img={iconClock}
          data={{
            title: 'Actualmente, este trámite se encuentra en proceso'
          }}
          handlerClick={() => {
            history.push(routes.student.procedures.home);
          }}
          handlerClose={() => {
            history.push(routes.student.procedures.home);
          }}
          show={isDuplicateRegister && !isError}
        />
      </ContainerProcedure>
    </div>
  );
};

export default EmployabilityCharterRegisterViewController;
